import { FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import { StepNavigator, Step } from 'dibs-elements/exports/StepNavigator';
import { useCheckoutSelector } from './reducers/root';
import { getCurrentPane, isStepAccessible } from './reducers/checkoutPaneReducer';
import { navigateToPane } from './actions/checkoutUIActions';
import { STEPS, type Step as TStep } from './constants/checkoutConstants';
import { useNavigateShippingOnTaxWarning } from './components/containers/hooks/navigationHooks';
import { trackStepBreadcrumbs } from './helpers/checkoutTracking';

import { CheckoutNavigation_buyer$key } from './__generated__/CheckoutNavigation_buyer.graphql';
import { CheckoutNavigation_transaction$key } from './__generated__/CheckoutNavigation_transaction.graphql';

import styles from './styles/CheckoutNavigation.scss';

type CheckoutStep = TStep;

const useCheckoutStepLink = (): { currentStep: CheckoutStep; clickable: CheckoutStep[] } => {
    const paneState = useCheckoutSelector(state => state.pane);
    const transactionState = useCheckoutSelector(state => state.transaction);
    const shippingState = useCheckoutSelector(state => state.shipping);

    const hasShipment = !!(transactionState.shippingAddressId && transactionState.shipmentType);
    const hasValidAddress =
        shippingState.isSelectedAddressValid && !paneState.isRedirectedDueToPreQuotes;

    const currentStep = getCurrentPane(paneState) as CheckoutStep;

    const clickable: CheckoutStep[] = [];

    const isDeliveryClickable =
        isStepAccessible(paneState, STEPS.STEP_DELIVERY) && hasShipment && hasValidAddress;
    const isPaymentClickable =
        isStepAccessible(paneState, STEPS.STEP_PAYMENT) && isDeliveryClickable;

    if (isStepAccessible(paneState, STEPS.STEP_SHIPPING)) {
        clickable.push(STEPS.STEP_SHIPPING);
    }
    if (isDeliveryClickable) {
        clickable.push(STEPS.STEP_DELIVERY);
    }
    if (isPaymentClickable) {
        clickable.push(STEPS.STEP_PAYMENT);
    }
    if (
        isStepAccessible(paneState, STEPS.STEP_REVIEW) &&
        isDeliveryClickable &&
        isPaymentClickable
    ) {
        clickable.push(STEPS.STEP_REVIEW);
    }

    return { currentStep, clickable };
};

const userFragment = graphql`
    fragment CheckoutNavigation_buyer on User {
        hasShippingAddresses
        hasBankAccounts
        hasCreditCards
    }
`;

const transactionFragment = graphql`
    fragment CheckoutNavigation_transaction on Transaction {
        hasBuyerTaxError
    }
`;

type Props = {
    isComputing: boolean;
    buyer: CheckoutNavigation_buyer$key | null;
    transaction: CheckoutNavigation_transaction$key | null;
};

export const CheckoutNavigation: FC<Props> = props => {
    const { isComputing } = props;

    const buyer = useFragment(userFragment, props.buyer);
    const transaction = useFragment(transactionFragment, props.transaction);
    const dispatch = useDispatch();
    const currentPane = useCheckoutSelector(state => state.pane.currentPane);
    const isPaneSetUp = useCheckoutSelector(
        state => state.pane.isSetUp && state.ui.authState === 'CHECKED'
    );
    const wasNavigatedDueTaxWarning = useCheckoutSelector(
        state => state.ui.wasNavigatedDueTaxWarning
    );
    const hasShippingAddresses = !!buyer?.hasShippingAddresses;
    const hasBankAccounts = !!buyer?.hasBankAccounts;
    const hasCreditCards = !!buyer?.hasCreditCards;

    useNavigateShippingOnTaxWarning({
        hasBuyerTaxError: transaction?.hasBuyerTaxError,
        wasNavigatedDueTaxWarning,
        onNavigateToPane: navigateToPane,
        hasShippingAddresses,
        hasCreditCards,
        currentPane,
        isComputing,
        dispatch,
    });

    const { currentStep, clickable } = useCheckoutStepLink();

    const isProcessing = useCheckoutSelector(state => state.ui.isProcessing) || false;

    function handleStepChange(step: CheckoutStep): void {
        const isClickable = clickable.includes(step) && !isProcessing;
        if (isClickable) {
            navigateToPane({
                pane: step,
                hasShippingAddresses,
                hasCreditCards,
                hasBankAccounts,
                currentPane,
                isComputing,
                dispatch,
            });

            trackStepBreadcrumbs(step);
        }
    }

    const navSteps: Step[] = [
        {
            label: (
                <FormattedMessage
                    id="checkout.CheckoutNavigation.shipping_step"
                    defaultMessage="Address"
                />
            ),
            onClick: () => handleStepChange(STEPS.STEP_SHIPPING),
            dataTn: 'checkout-navigation-shipping-link',
        },
        {
            label: (
                <FormattedMessage
                    id="checkout.CheckoutNavigation.delivery_step"
                    defaultMessage="Shipping"
                />
            ),
            onClick: () => handleStepChange(STEPS.STEP_DELIVERY),
            dataTn: 'checkout-navigation-delivery-link',
        },
        {
            label: (
                <FormattedMessage
                    id="checkout.CheckoutNavigation.payment_step"
                    defaultMessage="Payment"
                />
            ),
            onClick: () => handleStepChange(STEPS.STEP_PAYMENT),
            dataTn: 'checkout-navigation-payment-link',
        },
        {
            label: (
                <FormattedMessage
                    id="checkout.CheckoutNavigation.review_step"
                    defaultMessage="Review"
                />
            ),
            onClick: () => handleStepChange(STEPS.STEP_REVIEW),
            dataTn: 'checkout-navigation-review-link',
        },
    ];

    const navigatorStep = Object.values(STEPS).indexOf(currentStep);

    return (
        <div className={styles.wrapper}>
            <StepNavigator
                steps={navSteps}
                /**
                 * Don't show which step we're on until panes are fully setup.
                 */
                currentStep={isPaneSetUp ? navigatorStep : -1}
                dataTn="checkout-step-navigator"
            />
        </div>
    );
};
